import { useEffect, useState } from "react";
import { api, fmtErr } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { toast } from "sonner";
import { Pencil, Check, X } from "lucide-react";

// Inventory "Manage types / locations" — every value in use with its usage
// count. Renaming updates all items at once; renaming onto an existing value
// merges. kind: "types" | "locations".
export const ManageTypesDialog = ({ yachtId, kind = "types", onClose, onChanged }) => {
  const isLoc = kind === "locations";
  const base = isLoc ? "/inventory/locations" : "/inventory/types";
  const noun = isLoc ? "location" : "type";
  const tid = isLoc ? "location" : "type";
  const [rows, setRows] = useState(null);
  const [editing, setEditing] = useState(null); // name being renamed
  const [newName, setNewName] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`${base}?yacht_id=${yachtId}`);
      setRows(data);
    } catch (e) { toast.error(fmtErr(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [yachtId, kind]);

  const mergeTarget = editing && newName.trim() && newName.trim() !== editing
    ? (rows || []).find((r) => r.name.toLowerCase() === newName.trim().toLowerCase())
    : null;

  const saveRename = async () => {
    const to = newName.trim();
    if (!to || to === editing) { setEditing(null); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`${base}/rename`, {
        yacht_id: yachtId, from_name: editing, to_name: mergeTarget ? mergeTarget.name : to,
      });
      toast.success(mergeTarget
        ? `Merged ${data.renamed} item${data.renamed === 1 ? "" : "s"} into “${data.to}”`
        : `Renamed to “${data.to}” (${data.renamed} item${data.renamed === 1 ? "" : "s"})`);
      setEditing(null);
      await load();
      onChanged?.();
    } catch (e) { toast.error(fmtErr(e)); }
    setBusy(false);
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid={`manage-${kind}-dialog`}>
        <DialogHeader>
          <DialogTitle>{isLoc ? "Manage locations" : "Manage types"}</DialogTitle>
          <DialogDescription>
            Rename a {noun} to fix it on every item at once. Renaming onto an existing {noun} merges them.
            {isLoc
              ? " Locations exist only through items — once nothing uses one, it disappears."
              : " Preset types always stay available in the form; custom types disappear once no item uses them."}
          </DialogDescription>
        </DialogHeader>
        {rows === null ? (
          <div className="py-6 text-center text-sm text-zinc-500">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="py-6 text-center text-sm text-zinc-500" data-testid={`manage-${kind}-empty`}>No {noun}s in use yet.</div>
        ) : (
          <div className="max-h-[50vh] overflow-y-auto -mx-1 px-1 space-y-1.5">
            {rows.map((r) => (
              <div key={r.name} className="rounded-md border border-zinc-200 px-3 py-2" data-testid={`${tid}-row-${r.name}`}>
                {editing === r.name ? (
                  <div>
                    <div className="flex items-center gap-2">
                      <Input
                        value={newName}
                        onChange={(e) => setNewName(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") saveRename(); }}
                        autoFocus
                        className="h-8"
                        list={`manage-${kind}-targets`}
                        data-testid={`${tid}-rename-input`}
                      />
                      <datalist id={`manage-${kind}-targets`}>
                        {rows.filter((x) => x.name !== r.name).map((x) => <option key={x.name} value={x.name} />)}
                      </datalist>
                      <Button size="sm" className="h-8" onClick={saveRename} disabled={busy || !newName.trim() || newName.trim() === r.name} data-testid={`${tid}-rename-save`}>
                        <Check className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="outline" className="h-8" onClick={() => setEditing(null)} data-testid={`${tid}-rename-cancel`}>
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                    {mergeTarget && (
                      <div className="mt-1.5 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1" data-testid={`${tid}-merge-hint`}>
                        “{r.name}” ({r.count}) will be merged into “{mergeTarget.name}” ({mergeTarget.count}).
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <span className="text-sm font-medium break-words">{r.name}</span>
                      {r.preset && <span className="ml-2 rounded-full bg-zinc-100 text-zinc-500 text-[10px] px-1.5 py-0.5 uppercase tracking-wide">preset</span>}
                      <div className="text-[11px] text-zinc-400">
                        {r.count} item{r.count === 1 ? "" : "s"}{r.groups.length ? ` · ${r.groups.join(", ")}` : ""}
                      </div>
                    </div>
                    <Button
                      size="sm" variant="outline" className="h-8 shrink-0"
                      onClick={() => { setEditing(r.name); setNewName(r.name); }}
                      data-testid={`${tid}-rename-btn-${r.name}`}
                    >
                      <Pencil className="h-3.5 w-3.5 mr-1.5" />Rename
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid={`manage-${kind}-close`}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ManageTypesDialog;
